import { Creator } from '../../Creators/creatorTypes';
import { TableRow, TableColumn } from '../types';

/**
 * Available variables for email templates
 */
export interface TemplateVariables {
  // Creator info
  creator_name: string;
  creator_platform: string;
  creator_handle: string;
  creator_followers: string;
  creator_email: string;
  
  // Custom column values
  [key: string]: string;
}

/**
 * Extract variables from creator data and table columns
 */
export function extractVariablesFromCreator(
  creator: Creator,
  rowData: Record<string, any>,
  columns: TableColumn[]
): TemplateVariables {
  // Get primary platform data
  const tiktokData = creator.tiktok_data?.[0];
  const instagramData = creator.instagram_data?.[0];
  const twitchData = creator.twitch_data?.[0];
  
  // Determine primary platform
  const primaryPlatform = tiktokData ? 'TikTok' : 
                         instagramData ? 'Instagram' : 
                         twitchData ? 'Twitch' : 'Unknown';
  
  const primaryHandle = tiktokData?.handle || 
                       instagramData?.handle || 
                       twitchData?.handle || 
                       'Unknown';
  
  const followers = tiktokData?.followers_count || 
                   instagramData?.followers_count || 
                   twitchData?.followers_count || 
                   0;
  
  // Get email from contacts
  const email = creator.contacts?.find(c => c.type === 'email')?.value || '';
  
  // Build variables object
  const variables: TemplateVariables = {
    creator_name: primaryHandle,
    creator_platform: primaryPlatform,
    creator_handle: primaryHandle,
    creator_followers: formatNumber(followers),
    creator_email: email,
  };
  
  // Add custom column values using formatted names
  columns.forEach(column => {
    const cellData = rowData[column.id];
    
    // Extract actual value from CellData object or use plain value
    let value: any;
    if (cellData && typeof cellData === 'object' && 'value' in cellData) {
      // CellData object - extract the value property
      value = cellData.value;
    } else {
      // Plain value
      value = cellData;
    }
    
    if (value !== undefined && value !== null) {
      // Use the value as-is from the table cell
      // If it's a Shopify discount code, it should already be generated (e.g., "GUST20")
      // Don't process templates here - that happens only when creating the code in Shopify
      const finalValue = String(value);
      
      // Create variable name from column name (lowercase, replace spaces with underscores)
      const variableName = column.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      variables[variableName] = finalValue;
      
      // Also add with column ID for backward compatibility
      variables[column.id] = finalValue;
    }
  });
  
  return variables;
}

/**
 * Substitute variables in template string
 */
export function substituteVariables(
  template: string,
  variables: TemplateVariables
): string {
  let result = template;
  
  // Replace all {{variable}} instances
  Object.keys(variables).forEach(key => {
    const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
    result = result.replace(regex, variables[key] || '');
  });
  
  return result;
}

/**
 * Convert plain text with line breaks to HTML
 */
export function convertPlainTextToHtml(plainText: string): string {
  if (!plainText) return '';
  
  // Convert line breaks to HTML
  // First, normalize line endings
  let html = plainText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  
  // Convert double line breaks to paragraph breaks
  html = html.replace(/\n\n+/g, '</p><p>');
  
  // Convert single line breaks to <br> tags
  html = html.replace(/\n/g, '<br>');
  
  // Wrap in paragraph tags
  html = `<p>${html}</p>`;
  
  // Clean up empty paragraphs
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<p><br><\/p>/g, '<br>');
  
  return html;
}

/**
 * Substitute variables in TipTap JSON content
 */
export function substituteVariablesInTiptap(
  tiptapJson: any,
  variables: TemplateVariables
): any {
  if (!tiptapJson || typeof tiptapJson !== 'object') {
    return tiptapJson;
  }
  
  // Deep clone to avoid mutation
  const clone = JSON.parse(JSON.stringify(tiptapJson));
  
  // Recursively process all text nodes
  function processNode(node: any): any {
    if (node.type === 'text' && node.text) {
      node.text = substituteVariables(node.text, variables);
    }
    
    if (node.content && Array.isArray(node.content)) {
      node.content = node.content.map(processNode);
    }
    
    return node;
  }
  
  return processNode(clone);
}

/**
 * Convert TipTap JSON to HTML
 */
export function tiptapToHtml(tiptapJson: any): string {
  if (!tiptapJson || typeof tiptapJson !== 'object') {
    return String(tiptapJson || '');
  }
  
  function processNode(node: any): string {
    if (!node) return '';
    
    // Text node
    if (node.type === 'text') {
      let text = node.text || '';
      
      // Apply marks (bold, italic, etc.)
      if (node.marks && Array.isArray(node.marks)) {
        node.marks.forEach((mark: any) => {
          switch (mark.type) {
            case 'bold':
              text = `<strong>${text}</strong>`;
              break;
            case 'italic':
              text = `<em>${text}</em>`;
              break;
            case 'underline':
              text = `<u>${text}</u>`;
              break;
            case 'link':
              text = `<a href="${mark.attrs?.href || '#'}">${text}</a>`;
              break;
          }
        });
      }
      
      return text;
    }
    
    // Block nodes
    const content = node.content?.map(processNode).join('') || '';
    
    switch (node.type) {
      case 'doc':
        return content;
      case 'paragraph':
        return `<p>${content}</p>`;
      case 'heading':
        const level = node.attrs?.level || 1;
        return `<h${level}>${content}</h${level}>`;
      case 'bulletList':
        return `<ul>${content}</ul>`;
      case 'orderedList':
        return `<ol>${content}</ol>`;
      case 'listItem':
        return `<li>${content}</li>`;
      case 'blockquote':
        return `<blockquote>${content}</blockquote>`;
      case 'codeBlock':
        return `<pre><code>${content}</code></pre>`;
      case 'hardBreak':
        return '<br/>';
      case 'horizontalRule':
        return '<hr/>';
      default:
        return content;
    }
  }
  
  return processNode(tiptapJson);
}

/**
 * Format number with commas
 */
function formatNumber(num: number): string {
  return num.toLocaleString('en-US');
}

/**
 * Preview personalized email for a creator
 */
export interface PersonalizedEmailPreview {
  html: string;
  subject: string;
  plainText: string;
}

export function previewPersonalizedEmail(
  templateJson: any,
  subject: string | undefined,
  creator: Creator,
  rowData: Record<string, any>,
  columns: TableColumn[]
): PersonalizedEmailPreview {
  const variables = extractVariablesFromCreator(creator, rowData, columns);
  
  // Substitute in template
  const personalizedTiptap = substituteVariablesInTiptap(templateJson, variables);
  const html = tiptapToHtml(personalizedTiptap);
  
  // Substitute in subject
  const personalizedSubject = subject ? substituteVariables(subject, variables) : '';
  
  // Generate plain text version (strip HTML tags)
  const plainText = html.replace(/<[^>]*>/g, '').replace(/\n\n+/g, '\n\n').trim();
  
  return {
    html,
    subject: personalizedSubject,
    plainText
  };
}

